/**
 * settlements.ts — deterministic Commons settlement SITES (Approach A).
 *
 * Pure + seed-derived (exactly like terrain.ts): the same world seed yields
 * identical sites on every client AND the server. The client renders settlement
 * geometry from these; the server spawns NPCs at them. This file is VERBATIM-
 * MIRRORED in `server/src/lib/settlements.ts` (only the import extension differs).
 * The site POSITIONS / tiers / seeds / biomes MUST stay identical —
 * `verify:settlements` asserts it.
 */
import { terrainHeightBase, biomeAt } from './terrain.js';
import { placeCapitals, districtOffsets, capitalTheta0, civilizationOf, DISTRICT_NUDGE_M, DISTRICT_NUDGE_TRIES } from './capitals.js';
import { LANDFORM_TERRAIN_SEEDS } from './constants.js';

export type SettlementTier = 'city' | 'town' | 'hamlet' | 'capital' | 'district' | 'circuit';
export interface SettlementSite {
  id: number;
  centerX: number;
  centerZ: number;
  tier: SettlementTier;
  biome: string;
  siteSeed: number;
  /** Capital wave (2026-09-15): a district's capital site id (a capital carries its own id). */
  capitalId?: number;
  /** A district's ordinal 0..29 within its capital, ring order (shared/capitals.ts). */
  district?: number;
}

/**
 * Footprint radius (m) per tier. Used BOTH for inter-settlement spacing here AND
 * as the CityGenerator plan radius in `settlement_plan.ts`, so a settlement's
 * geometry never spills past the gap reserved for it (one source of truth). A
 * hamlet smaller than ~64 m produces no closed road blocks, so 70 is the floor.
 */
export const SETTLEMENT_FOOTPRINT: Record<SettlementTier, number> = { city: 240, town: 120, hamlet: 70, capital: 3230, district: 420, circuit: 500 };

/** FNV-1a(seed) → mulberry32 PRNG. Deterministic, string-seeded, never Math.random. */
export function settlementRng(seed: string): () => number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const WORLD_HALF = 1000000;   // 2048x2048 km shard half-extent (placement margin below)
const REGION_COUNT = 10;      // settled REGIONS — clusters keep towns walkably close
const REGION_R = 16000;       // sites scatter within ±16 km of a region center
const SLOPE_SAMPLE = 4;       // metres for the slope estimate
const MAX_SLOPE = 0.35;       // reject candidates steeper than this
const SPACING_MARGIN = 30;    // min clear gap between two settlement footprints

// 12 cities + 60 towns + 240 hamlets ≈ 312 sites, CLUSTERED into settled
// regions (a continental shard with uniform scatter would put neighbours ~60 km
// apart — unwalkable). Region 0 anchors the origin spawn cluster.
const TIER_PLAN: { tier: SettlementTier; count: number }[] = [
  { tier: 'city', count: 12 },
  { tier: 'town', count: 60 },
  { tier: 'hamlet', count: 240 },
];

// BASE field on purpose: the settlement plateaus in terrain.ts are built FROM
// these sites (terrainHeight → landformHeight → settlementSites), so the picker
// must not depend on the layer it feeds.
function slopeAt(seed: string, x: number, z: number): number {
  const h0 = terrainHeightBase(seed, x, z);
  const hx = terrainHeightBase(seed, x + SLOPE_SAMPLE, z);
  const hz = terrainHeightBase(seed, x, z + SLOPE_SAMPLE);
  return Math.max(Math.abs(hx - h0), Math.abs(hz - h0)) / SLOPE_SAMPLE;
}

/** Deterministic per-site seed from the accepted position + id (identical server-side). */
export function siteSeedFor(x: number, z: number, id: number): number {
  return (Math.imul(Math.round(x * 100) | 0, 131071)
    ^ Math.imul(Math.round(z * 100) | 0, 524287)
    ^ Math.imul(id + 1, 0x9e3779b1)) >>> 0;
}

/**
 * The settlement sites of a seed — MEMOIZED per seed (perf sweep 2026-08-22):
 * the generator samples terrain slope for every candidate (~40 ms), and two
 * per-frame callers (the vehicle HUD's nearCity, the music bucket) were
 * regenerating all 312 sites EVERY frame — 4.1 s of the main thread in a
 * 110 s wilderness walk. The array is shared: never sort or mutate it in
 * place (copy first).
 */
const siteMemo = new Map<string, SettlementSite[]>();
export function settlementSites(seed: string): SettlementSite[] {
  let s = siteMemo.get(seed);
  if (!s) { s = generateSettlementSites(seed); siteMemo.set(seed, s); }
  return s;
}

/**
 * The settled REGION centres of a seed, in generator order (region 0 = origin).
 * Replays the exact rng prefix `generateSettlementSites` consumes first, so the
 * centres are the ones the sites scattered around. The hydrology bake windows
 * its river extraction on these (terrain wave 2026-09-08).
 */
export function settlementRegionCenters(seed: string): Array<[number, number]> {
  const rng = settlementRng(seed);
  const regions: Array<[number, number]> = [[0, 0]];
  for (let i = 1; i < REGION_COUNT; i++) {
    regions.push([(rng() * 2 - 1) * (WORLD_HALF - REGION_R), (rng() * 2 - 1) * (WORLD_HALF - REGION_R)]);
  }
  return regions;
}
/** Scatter radius (m) of sites around a region centre. */
export const SETTLEMENT_REGION_R = REGION_R;

function generateSettlementSites(seed: string): SettlementSite[] {
  const rng = settlementRng(seed);
  // Region centers first (fixed rng order): region 0 = origin; the rest uniform.
  const regions: Array<[number, number]> = [[0, 0]];
  for (let i = 1; i < REGION_COUNT; i++) {
    regions.push([(rng() * 2 - 1) * (WORLD_HALF - REGION_R), (rng() * 2 - 1) * (WORLD_HALF - REGION_R)]);
  }
  const sites: SettlementSite[] = [];
  let id = 0;
  for (const { tier, count } of TIER_PLAN) {
    for (let k = 0; k < count; k++) {
      for (let attempt = 0; attempt < 400; attempt++) {
        let x: number; let z: number;
        if (tier === 'city' && k === 0) {
          // The FIRST city anchors near origin inside the plains pin (spawn hub).
          const ang = rng() * Math.PI * 2;
          const r = 120 + rng() * 220;
          x = Math.cos(ang) * r; z = Math.sin(ang) * r;
        } else {
          const [rcx, rcz] = regions[Math.floor(rng() * REGION_COUNT) % REGION_COUNT];
          x = rcx + (rng() * 2 - 1) * REGION_R;
          z = rcz + (rng() * 2 - 1) * REGION_R;
        }
        if (Math.hypot(x, z) > WORLD_HALF - 200) continue;
        if (slopeAt(seed, x, z) > MAX_SLOPE) continue;
        // footprint-aware: clear this tier's footprint + the neighbour's + a margin,
        // so no two settlements' geometry can overlap (a hamlet can't land inside the city).
        if (!sites.every((s) => Math.hypot(s.centerX - x, s.centerZ - z)
          >= SETTLEMENT_FOOTPRINT[tier] + SETTLEMENT_FOOTPRINT[s.tier] + SPACING_MARGIN)) continue;
        // 100x Commons: the plains pin no longer covers the map, so each settlement's
        // theme comes from its ACTUAL ground biome — architecture palette, flora, and
        // terrain all agree at the site. (The origin city sits in the pin → plains.)
        sites.push({ id, centerX: x, centerZ: z, tier, biome: biomeAt(seed, x, z), siteSeed: siteSeedFor(x, z, id) });
        id++;
        break;
      }
    }
  }
  // Capitals (2026-09-15): APPENDED after the classic sites so nothing above
  // ever moves — the picker uses no rng, the draws above are untouched. Commons
  // content only (the landform seed set, like the plateaus): a personal world or
  // a harness seed has no capitals, and the picker's "no legal site" throw is
  // reserved for the Commons bake.
  if (LANDFORM_TERRAIN_SEEDS.has(seed)) appendCapitals(seed, sites);
  return sites;
}

/**
 * Append capital A (civ a), capital B (civ b), the Grand Circuit campus and
 * the 60 district sites (30 per capital, ring order). A district is nudged
 * east by 1 cm until its siteSeed hashes to its capital's civ, so the
 * unchanged `civilizationOf` picks the right pack everywhere.
 */
function appendCapitals(seed: string, sites: SettlementSite[]): void {
  const firstId = sites.length;
  const place = placeCapitals(seed, sites, siteSeedFor, firstId);
  const push = (x: number, z: number, tier: SettlementTier, extra: { capitalId?: number; district?: number }): SettlementSite => {
    const id = sites.length;
    const s: SettlementSite = { id, centerX: x, centerZ: z, tier, biome: biomeAt(seed, x, z), siteSeed: siteSeedFor(x, z, id), ...extra };
    sites.push(s);
    return s;
  };
  const capA = push(place.a[0], place.a[1], 'capital', { capitalId: firstId });
  const capB = push(place.b[0], place.b[1], 'capital', { capitalId: firstId + 1 });
  push(place.campus[0], place.campus[1], 'circuit', {});
  for (const cap of [capA, capB]) {
    const civ = civilizationOf(cap.siteSeed);
    for (const d of districtOffsets(capitalTheta0(cap.siteSeed))) {
      const id = sites.length;
      let x = cap.centerX + d.dx;
      const z = cap.centerZ + d.dz;
      let tries = 0;
      while (civilizationOf(siteSeedFor(x, z, id)) !== civ) {
        if (++tries > DISTRICT_NUDGE_TRIES) throw new Error(`district ${id} of capital ${cap.id} never hashed to civ ${civ}`);
        x += DISTRICT_NUDGE_M;
      }
      push(x, z, 'district', { capitalId: cap.id, district: d.index });
    }
  }
}
